import { Controller } from "@hotwired/stimulus"

// Manages a set of inline-editable rows, at most one open at a time. Opening a
// row silently closes a clean open row; if the open row has unsaved changes it
// asks for confirmation (in-app dialog) before discarding them. Each row is a
// `row` target; its swap parts carry data-inline-edit-view / -field, and its
// editable control carries data-inline-edit-input. Toggling uses the Tailwind
// `hidden` class (the field is a flex container, so the `hidden` attribute would
// lose to `.flex`).
export default class extends Controller {
  static targets = ["row", "dialog"]

  edit(event) {
    event.preventDefault()
    const row = this.rowFor(event.target)
    if (!row || this.isOpen(row)) return

    if (this.openRows().some((r) => this.isDirty(r))) {
      this.pending = row
      this.dialogTarget.classList.remove("hidden")
      return
    }
    this.openRows().forEach((r) => this.close(r))
    this.open(row)
  }

  cancel(event) {
    event.preventDefault()
    this.close(this.rowFor(event.target))
  }

  confirmDiscard(event) {
    event.preventDefault()
    this.dialogTarget.classList.add("hidden")
    this.openRows().forEach((r) => this.close(r))
    if (this.pending) this.open(this.pending)
    this.pending = null
  }

  dismissDiscard(event) {
    event.preventDefault()
    this.dialogTarget.classList.add("hidden")
    this.pending = null
  }

  open(row) {
    this.parts(row, "view").forEach((el) => el.classList.add("hidden"))
    this.parts(row, "field").forEach((el) => el.classList.remove("hidden"))
    const input = row.querySelector("[data-inline-edit-input]")
    if (input) {
      input.dataset.initial = input.value
      input.focus()
      input.select()
    }
    row.dataset.editing = "true"
  }

  close(row) {
    this.parts(row, "field").forEach((el) => el.classList.add("hidden"))
    this.parts(row, "view").forEach((el) => el.classList.remove("hidden"))
    delete row.dataset.editing
  }

  rowFor(el) {
    return el.closest("[data-inline-edit-target='row']")
  }

  isOpen(row) {
    return row.dataset.editing === "true"
  }

  openRows() {
    return this.rowTargets.filter((row) => this.isOpen(row))
  }

  isDirty(row) {
    const input = row.querySelector("[data-inline-edit-input]")
    return !!input && input.value !== input.dataset.initial
  }

  parts(row, role) {
    return row.querySelectorAll(`[data-inline-edit-${role}]`)
  }
}
